/*
  Execution, wave 3 (screen 09) — pure helpers for the Board, the Matrix (#7) and site mode (#8). No React.

  - boardColumnOf(task)              the Board column of a main task (Update required waits with Waiting)
  - taskProgress(task, tree)         { done, total, checked } over the task's leaf sub-items
  - buildExecutionMatrix(tasks, tree, metric)  buildings × main tasks, each cell the % of leaf sub-items that are
                                     Done (metric 'done') or ticked on site (metric 'checked')
  - siteLocations(task, tree)        where a task is worked: the containers (floor / panel) holding its leaf items
  - deviceInfo(leaf)                 { name, part, address, room } read from a device sub-item
  - duplicateAddressIds(leaves)      ids of devices whose address repeats inside one location
*/
export const BOARD_COLUMNS = [
  { key: 'Not Started', label: 'Not started', tone: 'gray' },
  { key: 'In Progress', label: 'In progress', tone: 'blue' },
  { key: 'Waiting for Approval', label: 'Waiting for approval', tone: 'amber', hint: 'and Update required' },
  { key: 'Bottleneck', label: 'Bottleneck', tone: 'red', hint: 'Needs a decision' },
  { key: 'Done', label: 'Done', tone: 'navy' },
  { key: 'Not required', label: 'Not required', tone: 'gray' },
];

const text = (value) => String(value ?? '').trim();

export function boardColumnOf(task) {
  const status = text(task?.status) || 'Not Started';
  if (status === 'Update required') return 'Waiting for Approval';
  return BOARD_COLUMNS.some((column) => column.key === status) ? status : 'Not Started';
}

/** Leaf sub-items (no children of their own) under a task. */
export function leafItems(task, tree) {
  const leaves = [];
  const stack = [...(tree?.children?.get(task?.id) || [])];
  while (stack.length) {
    const item = stack.shift();
    const kids = tree?.children?.get(item.id) || [];
    if (kids.length) stack.unshift(...kids);
    else leaves.push(item);
  }
  return leaves;
}

export function taskProgress(task, tree) {
  const leaves = leafItems(task, tree);
  return {
    total: leaves.length,
    done: leaves.filter((leaf) => text(leaf.status) === 'Done').length,
    checked: leaves.filter((leaf) => text(leaf.checked_by)).length,
    // what is counted (09 #8): devices when the leaves are devices, else 'items' (the table counts direct sub-items)
    unit: leaves.length && leaves.every((leaf) => text(leaf.device_type)) ? 'devices' : 'items',
  };
}

const SHORT = {
  'Cable Pulling': 'Cable pull',
  'Cable Testing': 'Cable test',
  'Panel Termination': 'Panel term.',
  'Devices Installation': 'Dev. install',
  'Pre-Programming': 'Pre-prog.',
  'SOO Programming': 'SOO prog.',
  'AS-Built Drawings': 'AS-built',
  'Shop Drawings': 'Shop dwg',
  'Panel Design': 'Panel design',
  'Panel Manufacturing': 'Panel mfg.',
};
export const shortTaskLabel = (title) => SHORT[text(title)] || (text(title).length > 12 ? `${text(title).slice(0, 11)}…` : text(title));

/** Buildings × main tasks. metric 'done' | 'checked'. */
export function buildExecutionMatrix(tasks = [], tree = null, metric = 'done') {
  const mains = tasks.filter((task) => !task.parent_id);
  const columns = [];
  const counts = new Map(); // `${building}|${taskId}` → { total, hit }
  const buildings = new Map();
  mains.forEach((main) => {
    const leaves = leafItems(main, tree);
    const zoned = leaves.filter((leaf) => text(leaf.zone));
    if (!zoned.length) return;
    columns.push({ key: main.id, label: shortTaskLabel(main.title), title: text(main.title), task: main });
    zoned.forEach((leaf) => {
      const building = text(leaf.zone);
      if (!buildings.has(building)) buildings.set(building, { key: building, label: building });
      const key = `${building}|${main.id}`;
      const entry = counts.get(key) || { total: 0, hit: 0, notRequired: 0 };
      if (text(leaf.status) === 'Not required') {
        entry.notRequired += 1;
      } else {
        entry.total += 1;
        if (metric === 'checked' ? text(leaf.checked_by) : text(leaf.status) === 'Done') entry.hit += 1;
      }
      counts.set(key, entry);
    });
  });
  const rows = [...buildings.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  const cell = (rowKey, columnKey) => {
    const entry = counts.get(`${rowKey}|${columnKey}`);
    if (!entry) return null;
    // everything here is "Not required": no percentage, nothing to open
    if (!entry.total) return { label: 'n/a', tone: 'gray', title: `${entry.notRequired} not required`, disabled: true, ...entry };
    return { value: Math.round((entry.hit / entry.total) * 100), title: `${entry.hit} of ${entry.total} ${metric === 'checked' ? 'checked on site' : 'done'}`, ...entry };
  };
  return { rows, columns, cell };
}

/** "Villa 3 (E) · Ground Floor · HA Panel GF" from a sub-item's zones (else its title after the task name). */
export function locationLabel(item, mainTitle = '') {
  const parts = [text(item?.zone), text(item?.zone_2), text(item?.zone_3)].filter(Boolean);
  if (parts.length) return parts.join(' · ');
  const title = text(item?.title);
  return mainTitle && title.startsWith(`${mainTitle} - `) ? title.slice(mainTitle.length + 3).split(' - ').join(' · ') : title;
}

/**
 * The places a task is worked: containers whose children are leaves (a panel with its devices, or the task
 * itself when its sub-items are floors). [{ id, label, container, leaves, done, checked, total }]
 */
export function siteLocations(task, tree) {
  const children = tree?.children?.get(task?.id) || [];
  if (!children.length) return [];
  const groups = [];
  const visit = (node) => {
    const kids = tree?.children?.get(node.id) || [];
    const leafKids = kids.filter((kid) => !(tree?.children?.get(kid.id) || []).length);
    if (leafKids.length && node.id !== task.id) {
      groups.push({ id: node.id, container: node, label: locationLabel(node, task.title), leaves: leafKids });
    }
    kids.filter((kid) => (tree?.children?.get(kid.id) || []).length).forEach(visit);
  };
  const topLeaves = children.filter((kid) => !(tree?.children?.get(kid.id) || []).length);
  if (topLeaves.length) groups.push({ id: task.id, container: task, label: 'Whole task', leaves: topLeaves });
  children.filter((kid) => (tree?.children?.get(kid.id) || []).length).forEach(visit);
  return groups.map((group) => ({
    ...group,
    total: group.leaves.length,
    done: group.leaves.filter((leaf) => text(leaf.status) === 'Done').length,
    checked: group.leaves.filter((leaf) => text(leaf.checked_by)).length,
  }));
}

/** Device sub-item → { part, name, address, room }; other leaves → { name: their location }. */
export function deviceInfo(leaf, mainTitle = '') {
  const raw = text(leaf?.device_type);
  if (!raw) return { part: '', name: locationLabel(leaf, mainTitle) || text(leaf?.title), address: '', room: '' };
  const addressMatch = raw.match(/\(([^()]*\d[^()]*)\)\s*$/);
  const address = addressMatch ? addressMatch[1].trim() : '';
  const withoutAddress = addressMatch ? raw.slice(0, addressMatch.index).trim() : raw;
  const dash = withoutAddress.indexOf(' - ');
  const part = dash > 0 ? withoutAddress.slice(0, dash).trim() : '';
  const name = dash > 0 ? withoutAddress.slice(dash + 3).trim() : withoutAddress;
  const room = text(leaf?.zone_3).split(' / ')[0] || '';
  return { part, name: name || part || 'Device', address, room };
}

export function duplicateAddressIds(leaves = []) {
  const byAddress = new Map();
  leaves.forEach((leaf) => {
    const { address } = deviceInfo(leaf);
    if (!address) return;
    if (!byAddress.has(address)) byAddress.set(address, []);
    byAddress.get(address).push(leaf.id);
  });
  return new Set([...byAddress.values()].filter((ids) => ids.length > 1).flat());
}
